import {useAppSelector} from '@/redux-store/store';
import {Delete, PhotoCamera} from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Typography,
  Box
} from '@mui/material';
import {Form, Formik} from 'formik';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import LocationValidationSchema, {
  TITLE_MAX_LENGTH,
  DESC_MAX_LENGTH
} from '@/schemas/admin/locations';
import {useDispatch} from 'react-redux';
import {
  initialState,
  resetLocationFormData,
  setLocationFormData
} from '@/redux-store/slices/admin/locations';
import {LocationInput} from '@/components/forms/input/location';
import {Location} from '@/schemas/sub-schemas';
import {addLocation} from '@/rest/admin/locations/add-location';
import {SportSelectInput} from '@/components/forms/input/sports';
import {SportLocationForm} from '@/types/sportLocation';
import TypographyTitle from '@/components/ui/typography-title';
interface LocationAddFormProps {
  onCancel?: () => void;
  onSubmit?: (newLocation: SportLocationForm) => void;
  onError?: (error: Error) => void;
}

const LocationAddForm: React.FC<LocationAddFormProps> = ({
  onCancel,
  onError,
  onSubmit
}) => {
  const initialValues = useAppSelector(
    (state) => state.locations.locationCreateForm
  );
  const userValues = useAppSelector((state) => state.userInfos.userInfos);
  const {t} = useTranslation();

  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const dispatch = useDispatch();

  const AddLocationValidationSchema = LocationValidationSchema(t);

  const handleLocationSelected = (location: Location) => {
    dispatch(setLocationFormData({location}));
  };

  const resetFormData = useCallback(() => {
    dispatch(resetLocationFormData());
    setFileSizeError(null);
  }, [dispatch]);

  const handleImageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 0.5) {
          setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'kB'}));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          dispatch(
            setLocationFormData({
              picture: reader.result as string
            })
          );
          setFileSizeError(null);
        };
      }
    },
    [dispatch, t]
  );

  const submitForm = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: any) => {
      const {title, description, location, sports} = values;

      let updatedValues: SportLocationForm = {
        title: title,
        description: description,
        location: {
          lat: location.lng,
          lng: location.lat,
          label: location.label
        },
        sports: sports
      };

      if (initialValues.picture) {
        updatedValues = {
          ...updatedValues,
          picture: initialValues.picture
        };
      }

      addLocation(updatedValues)
        .then((response) => {
          if (response) {
            if (onSubmit) {
              onSubmit(response);
              resetFormData();
            }
          } else {
            if (onError) {
              onError(new Error('Error while adding location'));
            }
          }
        })
        .catch((error) => {
          console.error('Error:', error);
          if (onError) {
            onError(error as Error);
          }
        });
    },
    [initialValues.picture, onError, onSubmit, resetFormData]
  );

  const handleDeleteImage = useCallback(() => {
    dispatch(setLocationFormData({picture: null}));
  }, [dispatch]);

  return (
    <>
      <Formik
        //enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={AddLocationValidationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          submitForm(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          resetForm,
          validateForm,
          setFieldValue,
          setFieldTouched
        }) => {
          // console.log(errors);
          return (
            <Form>
              <TypographyTitle textKey="add_location" />
              <Grid container spacing={2} my={1} marginTop={1}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    value={values.title}
                    label={t('title')}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setLocationFormData({
                          title: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <FormHelperText>
                    {values.title.length} / {TITLE_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    value={values.description}
                    label={t('description')}
                    multiline
                    maxRows={4}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setLocationFormData({
                          description: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                  <FormHelperText>
                    {values.description.length} / {DESC_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <LocationInput
                    name="location"
                    label={t('location')}
                    value={values.location}
                    error={errors['location']}
                    touched={touched['location']}
                    onBlur={() => {
                      setFieldTouched('location', true);
                    }}
                    onChange={(location) => {
                      setFieldValue('location', location);
                      if (location) {
                        handleLocationSelected(location);
                      }
                    }}
                    initialLocation={
                      initialValues.location || userValues?.location
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={5}>
                <Grid item xs={12}>
                  <SportSelectInput
                    name="sports"
                    value={values.sports}
                    error={errors.sports}
                    touched={touched.sports}
                    onBlur={handleBlur}
                    onChange={(sportIds) => {
                      setFieldValue('sports', sportIds);
                      dispatch(
                        setLocationFormData({
                          sports: sportIds
                        })
                      );
                    }}
                    multiple={true}
                    optional
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                marginTop={4}
                justifyContent={'center'}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{marginBottom: 2}}
                  >
                    {t('location_image')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteImage}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('delete_photo')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <label htmlFor="logo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('upload_photo')}
                    </Button>
                  </label>

                  <label htmlFor="phote-delete-xs">
                    <IconButton
                      aria-label={t('delete_photo')}
                      color="error"
                      onClick={handleDeleteImage}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </label>
                  <input
                    id="logo-upload"
                    name="logo"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                  <label htmlFor="photo-upload-xs">
                    <IconButton
                      aria-label={t('upload_photo')}
                      color="secondary"
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '1px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="photo-upload-xs"
                    name="logo"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: {xs: 0, sm: 1}
                  }}
                >
                  {initialValues.picture && (
                    <Box
                      component="img"
                      src={initialValues.picture}
                      alt={t('location_image')}
                      sx={{
                        maxWidth: {xs: '150px', sm: '200px'},
                        maxHeight: '100px',
                        marginTop: {xs: 2, sm: 0}
                      }}
                    />
                  )}
                </Grid>
              </Grid>

              {fileSizeError && (
                <Typography align="center" color="error" marginTop={2}>
                  {fileSizeError}
                </Typography>
              )}
              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                justifyContent="center"
                spacing={2}
                sx={{
                  marginTop: {xs: 2, sm: 5},
                  marginBottom: 5
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',

                    order: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      dispatch(resetLocationFormData());
                      resetForm({
                        values: initialState.locationCreateForm
                      });
                      if (onCancel) {
                        onCancel();
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    order: {xs: 2, sm: 2},
                    marginBottom: {xs: 2, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: initialState.locationCreateForm
                      });
                      validateForm();
                    }}
                    sx={{width: {xs: '50%', sm: '80%'}}}
                  >
                    {t('reset')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    order: {xs: 3, sm: 3}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!!fileSizeError}
                    sx={{width: {xs: '50%', sm: '80%'}}}
                  >
                    {t('create')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default LocationAddForm;
